// Web Push 通知服务
import request from "./request";
import { myErrorReport } from "./errorReport";
import { env } from "../config/env";

// 类型定义
export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface NotificationData {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  data?: any;
  actions?: any[];
}

export interface WebPushConfig {
  vapidPublicKey: string;
  enableMock: boolean;
}

// 配置
const WEB_PUSH_CONFIG: WebPushConfig = {
  vapidPublicKey:
    env.VAPID_PUBLIC_KEY ||
    "BPOHc1Vk5v2RhFpJECqwWMjTaIU6O60K7DXUpAcDWCHeLIE3F5Y3MGOu6crALYEgFav_IlkuQ0oFnILCCjS3Hwg",
  enableMock: env.DEV_MODE,
};

/**
 * 将 base64 字符串转换为 Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const buffer = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) {
    buffer[i] = raw.charCodeAt(i);
  }
  return buffer;
}

/**
 * 检查浏览器是否支持 Web Push
 */
export function isWebPushSupported(): boolean {
  try {
    const hasSW = "serviceWorker" in navigator;
    const hasPush = "PushManager" in window;
    const hasNotif = "Notification" in window;

    // iOS Safari 支持检查
    const isIosSafari =
      /^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
      /Mac OS|iPhone|iPad/.test(navigator.platform);

    const iOSVersion = (() => {
      const match = navigator.userAgent.match(/OS (\d+)_/);
      return match ? parseInt(match[1], 10) : 0;
    })();

    const iOSPushOK = isIosSafari && iOSVersion >= 16;

    return (
      hasSW &&
      hasPush &&
      hasNotif &&
      (!/iP(hone|ad|od)/.test(navigator.platform) || iOSPushOK)
    );
  } catch (error) {
    myErrorReport(error as Error, { context: "isWebPushSupported" });
    return false;
  }
}

/**
 * 请求通知权限
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  try {
    if (!("Notification" in window)) {
      return "denied";
    }

    const current = Notification.permission;
    if (current === "granted") {
      return current;
    }

    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    myErrorReport(error as Error, { context: "requestNotificationPermission" });
    return "denied";
  }
}

/**
 * 应用初始化时订阅推送
 */
export async function subscribeAppInit(): Promise<PushSubscription | null> {
  try {
    if (!isWebPushSupported()) {
      console.warn("Web Push is not supported in this browser");
      return null;
    }

    const permission = await requestNotificationPermission();
    if (permission !== "granted") {
      console.warn("Notification permission denied");
      return null;
    }

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          WEB_PUSH_CONFIG.vapidPublicKey
        ) as BufferSource,
      });
    }

    // 发送订阅信息到服务器
    if (WEB_PUSH_CONFIG.enableMock) {
      console.log("Mock: Web Push subscription sent to server", subscription);
    } else {
      await request("/api/web_push/subscribe", subscription, {
        method: "POST",
      });
    }

    return subscription;
  } catch (error) {
    myErrorReport(error as Error, { context: "subscribeAppInit" });
    return null;
  }
}

/**
 * 用户登录后更新推送订阅
 */
export async function subscribeHasLogin(): Promise<PushSubscription | null> {
  try {
    if (!isWebPushSupported()) {
      console.warn("Web Push is not supported in this browser");
      return null;
    }

    const permission = await requestNotificationPermission();
    if (permission !== "granted") {
      console.warn("Notification permission denied");
      return null;
    }

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          WEB_PUSH_CONFIG.vapidPublicKey
        ) as BufferSource,
      });
    }

    // 更新用户ID关联的推送订阅
    if (WEB_PUSH_CONFIG.enableMock) {
      console.log(
        "Mock: Web Push subscription updated with user ID",
        subscription
      );
    } else {
      await request("/api/web_push/updateUserId", subscription, {
        method: "POST",
      });
    }

    return subscription;
  } catch (error) {
    myErrorReport(error as Error, { context: "subscribeHasLogin" });
    return null;
  }
}

/**
 * 发送测试通知
 */
export async function sendTestNotification(
  data: NotificationData
): Promise<void> {
  try {
    if (!isWebPushSupported()) {
      console.warn("Web Push is not supported in this browser");
      return;
    }

    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(data.title, {
      body: data.body,
      icon: data.icon,
      tag: data.tag,
      data: data.data,
      // actions: data.actions, // 暂时注释，因为 TypeScript 类型定义问题
    });

    console.log("Test notification sent successfully");
  } catch (error) {
    myErrorReport(error as Error, {
      context: "sendTestNotification",
      data: { title: data.title, body: data.body },
    });
    console.error("Error showing notification:", error);
  }
}

/**
 * 取消推送订阅
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    if (!isWebPushSupported()) {
      return false;
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      const success = await subscription.unsubscribe();

      if (success && !WEB_PUSH_CONFIG.enableMock) {
        await request(
          "/api/web_push/unsubscribe",
          { endpoint: subscription.endpoint },
          {
            method: "POST",
          }
        );
      }

      return success;
    }

    return true;
  } catch (error) {
    myErrorReport(error as Error, { context: "unsubscribeFromPush" });
    return false;
  }
}

/**
 * 检查当前推送订阅状态
 */
export async function getPushSubscriptionStatus(): Promise<{
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  subscription: PushSubscription | null;
}> {
  try {
    const isSupported = isWebPushSupported();
    const permission = Notification.permission;
    let isSubscribed = false;
    let subscription: PushSubscription | null = null;

    if (isSupported) {
      const registration = await navigator.serviceWorker.ready;
      subscription = await registration.pushManager.getSubscription();
      isSubscribed = !!subscription;
    }

    return {
      isSupported,
      permission,
      isSubscribed,
      subscription,
    };
  } catch (error) {
    myErrorReport(error as Error, { context: "getPushSubscriptionStatus" });
    return {
      isSupported: false,
      permission: "denied",
      isSubscribed: false,
      subscription: null,
    };
  }
}

// 默认导出配置
export default {
  isWebPushSupported,
  requestNotificationPermission,
  subscribeAppInit,
  subscribeHasLogin,
  sendTestNotification,
  unsubscribeFromPush,
  getPushSubscriptionStatus,
};
